import React, { useEffect, useState } from 'react';
import { Plus, Search, GraduationCap, Edit2, Trash2, BookOpen, Building2, X, Users, CheckCircle, Clock, Save } from 'lucide-react';
import { trainingProgramsService } from '../services/trainingPrograms';
import { organizationsService } from '../services/organizations';
import { enrollmentsService } from '../services/enrollments';
import { TrainingProgram, Organization } from '../types';
import { useAuth } from '../contexts/AuthContext';

// Type pour les stats par groupe
interface GroupStats {
  groupName: string;
  totalEnrollments: number;
  completedEnrollments: number;
  lastCompletedAt: Date | null;
}

// Type pour les stats de programme
interface ProgramStats {
  totalEnrollments: number;
  completedEnrollments: number;
  averageProgress: number;
  completionRate: number;
}

// Helper function to format duration in hours and minutes
const formatDuration = (decimalHours: number): string => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);

  if (hours === 0 && minutes === 0) return 'Durée non définie';
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
};

// Mini Donut Chart Component
interface MiniDonutProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

const MiniDonut: React.FC<MiniDonutProps> = ({ percentage, size = 48, strokeWidth = 6 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Couleur basée sur le pourcentage
  const getColor = (pct: number) => {
    if (pct >= 75) return { stroke: '#10b981', bg: '#d1fae5' }; // green
    if (pct >= 50) return { stroke: '#f59e0b', bg: '#fef3c7' }; // amber
    if (pct >= 25) return { stroke: '#f97316', bg: '#ffedd5' }; // orange
    return { stroke: '#ef4444', bg: '#fee2e2' }; // red
  };

  const colors = getColor(percentage);

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200 dark:text-gray-600"
        />
        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <span className="absolute text-xs font-bold text-gray-700 dark:text-gray-300">
        {percentage}%
      </span>
    </div>
  );
};

export const Programs: React.FC = () => {
  const { profile, isSuperAdmin } = useAuth();
  const isClient = profile?.role === 'client';

  const [programs, setPrograms] = useState<TrainingProgram[]>([]);
  const [programStats, setProgramStats] = useState<Map<string, ProgramStats>>(new Map());
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<TrainingProgram | null>(null);
  const [viewingProgram, setViewingProgram] = useState<TrainingProgram | null>(null);
  const [groupStats, setGroupStats] = useState<GroupStats[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    loadData();
  }, [profile]);

  useEffect(() => {
    if (selectedOrgId) {
      loadProgramsForOrg(selectedOrgId);
    } else {
      setPrograms([]);
    }
  }, [selectedOrgId]);

  const loadData = async () => {
    try {
      // Pour les clients, utiliser directement leur organizationId
      if (isClient && profile?.organizationId) {
        setSelectedOrgId(profile.organizationId);
        setLoading(false);
        return;
      }

      // Pour les super admins, charger toutes les organisations
      const orgs = await organizationsService.getAll();
      setOrganizations(orgs);
      if (orgs.length > 0) {
        setSelectedOrgId(orgs[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProgramsForOrg = async (orgId: string) => {
    try {
      const data = await trainingProgramsService.getByOrganization(orgId);
      setPrograms(data);

      // Load stats for each program
      const statsMap = new Map<string, ProgramStats>();
      await Promise.all(
        data.map(async (program) => {
          try {
            const stats = await enrollmentsService.getProgramStats(program.id);
            statsMap.set(program.id, stats);
          } catch (err) {
            console.warn(`Error loading stats for program ${program.id}:`, err);
            statsMap.set(program.id, {
              totalEnrollments: 0,
              completedEnrollments: 0,
              averageProgress: 0,
              completionRate: 0
            });
          }
        })
      );
      setProgramStats(statsMap);
    } catch (error) {
      console.error('Error loading programs:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer ce programme ? Cette action est irréversible.')) {
      return;
    }

    try {
      await trainingProgramsService.delete(id);
      setPrograms(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting program:', error);
      alert('Impossible de supprimer le programme');
    }
  };

  const handleEdit = (program: TrainingProgram) => {
    setEditingProgram(program);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingProgram(null);
    setIsModalOpen(true);
  };

  const handleViewDetails = async (program: TrainingProgram) => {
    setViewingProgram(program);
    setLoadingStats(true);
    try {
      const stats = await enrollmentsService.getProgramGroupStats(program.id);
      setGroupStats(stats);
    } catch (error) {
      console.error('Error loading group stats:', error);
      setGroupStats([]);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSave = async (programData: Partial<TrainingProgram>) => {
    try {
      if (editingProgram) {
        await trainingProgramsService.update(editingProgram.id, programData);
      } else {
        await trainingProgramsService.create({
          ...programData,
          organizationId: selectedOrgId
        } as Omit<TrainingProgram, 'id' | 'createdAt' | 'updatedAt'>);
      }
      setIsModalOpen(false);
      loadProgramsForOrg(selectedOrgId);
    } catch (error) {
      console.error('Error saving program:', error);
      alert('Impossible de sauvegarder le programme');
    }
  };

  const handleUpdateDuration = async (programId: string, duration: number | undefined) => {
    try {
      await trainingProgramsService.update(programId, { estimatedDurationHours: duration });
      // Update local state
      setPrograms(prev => prev.map(p =>
        p.id === programId ? { ...p, estimatedDurationHours: duration } : p
      ));
      // Update viewing program if it's the same
      if (viewingProgram?.id === programId) {
        setViewingProgram(prev => prev ? { ...prev, estimatedDurationHours: duration } : null);
      }
    } catch (error) {
      console.error('Error updating duration:', error);
      throw error;
    }
  };

  // Filter programs
  const filteredPrograms = programs.filter(program => {
    return !searchQuery ||
      program.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      program.description?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Formations
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {isClient ? 'Consultez vos formations disponibles' : 'Gérez les formations par organisation'}
          </p>
        </div>
        {!isClient && (
          <button
            onClick={handleCreate}
            disabled={!selectedOrgId}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" />
            Nouvelle formation
          </button>
        )}
      </div>

      {/* Organization Selector & Filters (hidden for clients) */}
      <div className="flex flex-col sm:flex-row gap-4">
        {!isClient && (
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-400" />
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
            >
              <option value="">Sélectionner une organisation</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher une formation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </div>
      </div>

      {/* Programs List */}
      {!selectedOrgId ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{isClient ? 'Aucune organisation associée à votre compte' : 'Sélectionnez une organisation pour voir ses formations'}</p>
        </div>
      ) : filteredPrograms.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Aucune formation pour cette organisation</p>
          {!isClient && (
            <button
              onClick={handleCreate}
              className="mt-4 text-primary hover:underline"
            >
              Créer la première formation
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrograms.map((program) => (
            <ProgramCard
              key={program.id}
              program={program}
              stats={programStats.get(program.id)}
              onEdit={() => handleEdit(program)}
              onDelete={() => handleDelete(program.id)}
              onClick={() => handleViewDetails(program)}
              readOnly={isClient}
            />
          ))}
        </div>
      )}

      {/* Modal de création/édition */}
      {isModalOpen && (
        <ProgramModal
          program={editingProgram}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
        />
      )}

      {/* Modal de détails par groupe */}
      {viewingProgram && (
        <ProgramGroupsModal
          program={viewingProgram}
          groupStats={groupStats}
          loading={loadingStats}
          onClose={() => setViewingProgram(null)}
          onUpdateDuration={handleUpdateDuration}
          readOnly={isClient}
        />
      )}
    </div>
  );
};

// Program Card Component
interface ProgramCardProps {
  program: TrainingProgram;
  stats?: ProgramStats;
  onEdit: () => void;
  onDelete: () => void;
  onClick: () => void;
  readOnly?: boolean;
}

const ProgramCard: React.FC<ProgramCardProps> = ({ program, stats, onEdit, onDelete, onClick, readOnly = false }) => {
  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md hover:border-primary/50 transition-all"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {program.name}
            </h3>
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
              program.isActive
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
            }`}>
              {program.isActive ? 'Actif' : 'Inactif'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Completion Rate Donut */}
          {stats && stats.totalEnrollments > 0 && (
            <MiniDonut percentage={stats.completionRate} size={44} strokeWidth={5} />
          )}
          {!readOnly && (
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="p-2 text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {program.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">
          {program.description}
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <BookOpen className="w-4 h-4" />
          <span>
            {program.estimatedDurationHours
              ? formatDuration(program.estimatedDurationHours)
              : 'Durée non définie'}
          </span>
        </div>
        {/* Enrollment count */}
        {stats && (
          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <Users className="w-4 h-4" />
            <span>{stats.totalEnrollments} inscrit{stats.totalEnrollments > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Program Modal Component
interface ProgramModalProps {
  program: TrainingProgram | null;
  onClose: () => void;
  onSave: (data: Partial<TrainingProgram>) => void;
}

const ProgramModal: React.FC<ProgramModalProps> = ({ program, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: program?.name || '',
    description: program?.description || '',
    estimatedDurationHours: program?.estimatedDurationHours || '',
    isActive: program?.isActive ?? true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        name: formData.name,
        description: formData.description || undefined,
        estimatedDurationHours: formData.estimatedDurationHours
          ? Number(formData.estimatedDurationHours)
          : undefined,
        isActive: formData.isActive
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {program ? 'Modifier la formation' : 'Nouvelle formation'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nom de la formation *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Durée estimée (heures)
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={formData.estimatedDurationHours}
              onChange={(e) => setFormData({ ...formData, estimatedDurationHours: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
              Formation active
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Program Groups Modal Component
interface ProgramGroupsModalProps {
  program: TrainingProgram;
  groupStats: GroupStats[];
  loading: boolean;
  onClose: () => void;
  onUpdateDuration: (programId: string, duration: number | undefined) => Promise<void>;
  readOnly?: boolean;
}

const ProgramGroupsModal: React.FC<ProgramGroupsModalProps> = ({ program, groupStats, loading, onClose, onUpdateDuration, readOnly = false }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Convert decimal hours to hours and minutes
  const initialHours = program.estimatedDurationHours ? Math.floor(program.estimatedDurationHours) : 0;
  const initialMinutes = program.estimatedDurationHours
    ? Math.round((program.estimatedDurationHours - initialHours) * 60)
    : 0;

  const [hoursValue, setHoursValue] = useState<string>(initialHours > 0 ? initialHours.toString() : '');
  const [minutesValue, setMinutesValue] = useState<string>(initialMinutes > 0 ? initialMinutes.toString() : '');
  const [savingDuration, setSavingDuration] = useState(false);
  const [durationSaved, setDurationSaved] = useState(false);

  const handleSaveDuration = async () => {
    setSavingDuration(true);
    try {
      const hours = parseInt(hoursValue) || 0;
      const minutes = parseInt(minutesValue) || 0;
      // Convert to decimal hours
      const duration = hours + (minutes / 60);
      await onUpdateDuration(program.id, duration > 0 ? duration : undefined);
      setDurationSaved(true);
      setTimeout(() => setDurationSaved(false), 2000);
    } catch (error) {
      console.error('Error saving duration:', error);
    } finally {
      setSavingDuration(false);
    }
  };

  const totalEnrollments = groupStats.reduce((sum, g) => sum + g.totalEnrollments, 0);
  const totalCompleted = groupStats.reduce((sum, g) => sum + g.completedEnrollments, 0);

  // Filtrer les groupes par recherche
  const filteredGroups = groupStats.filter(group =>
    group.groupName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Formater la date
  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {program.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Détails par groupe
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Duration field */}
          <div className="px-6 pt-4 pb-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Durée moyenne :</span>
              </div>
              {readOnly ? (
                <span className="text-sm text-gray-900 dark:text-white">
                  {program.estimatedDurationHours ? formatDuration(program.estimatedDurationHours) : 'Non définie'}
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={hoursValue}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setHoursValue(val);
                    }}
                    placeholder="0"
                    className="w-14 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">h</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={minutesValue}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      const num = parseInt(val);
                      if (val && num > 59) {
                        setMinutesValue('59');
                      } else {
                        setMinutesValue(val);
                      }
                    }}
                    placeholder="0"
                    className="w-14 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">min</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveDuration();
                    }}
                    disabled={savingDuration}
                    className={`flex items-center gap-1 px-3 py-1 text-sm rounded-lg transition-colors ${
                      durationSaved
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-primary/10 text-primary hover:bg-primary/20'
                    }`}
                  >
                    {savingDuration ? (
                      <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    ) : durationSaved ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Enregistré
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Enregistrer
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : groupStats.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun apprenant inscrit à cette formation</p>
            </div>
          ) : (
            <>
              {/* Stats globales */}
              <div className="p-6 pb-0 flex-shrink-0">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-1">
                      <Users className="w-4 h-4" />
                      <span className="text-sm font-medium">Total inscrits</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{totalEnrollments}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Formations achevées</span>
                    </div>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-300">{totalCompleted}</p>
                  </div>
                </div>

                {/* Barre de recherche */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher un groupe..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                </div>

                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Groupes ({filteredGroups.length}{searchQuery && ` / ${groupStats.length}`})
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Triés par complétion la plus récente
                  </span>
                </div>
              </div>

              {/* Liste des groupes avec scroll */}
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                {filteredGroups.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucun groupe trouvé pour "{searchQuery}"</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredGroups.map((group) => (
                      <div
                        key={group.groupName}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                            <Users className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {group.groupName}
                            </span>
                            {group.lastCompletedAt && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Dernière complétion : {formatDate(group.lastCompletedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {group.totalEnrollments} inscrit{group.totalEnrollments > 1 ? 's' : ''}
                          </span>
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                            <CheckCircle className="w-3.5 h-3.5" />
                            {group.completedEnrollments}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};
